<template>
    <div
        class="workforce-plan-row-fragment"
        :data-plan-employee-id="employee.employee_id"
        :class="{
            'workforce-plan-row-fragment--inspected': inspected,
            'workforce-plan-row-fragment--row-selected': rowSelected,
            'workforce-plan-row-fragment--focused': focused
        }"
        role="row"
    >
        <button
            :id="api.rowSelectorDomId(employee)"
            type="button"
            class="workforce-plan-row-selector"
            :class="{
                'is-active': rowHasSelection,
                'is-selected': rowSelected
            }"
            :title="`Выделить строку ${api.employeeFullName(employee)}. Ctrl + клик — добавить ещё, Ctrl + C — скопировать ФИО`"
            :aria-label="`Выделить строку ${api.employeeFullName(employee)}`"
            :aria-pressed="rowSelected ? 'true' : 'false'"
            @mousedown.stop
            @click.stop="api.selectEmployeeRow(employee, $event)"
        >
            <span>{{ rowNumber }}</span>
        </button>
        <div
            class="workforce-plan-employee"
            :class="{
                'workforce-plan-employee--itr': isItr,
                'workforce-plan-employee--loupe': isOutOfPlanning
            }"
            :title="employeeTitle"
            :style="isOutOfPlanning ? {'--employee-loupe-background': currentCellPresentation.style.backgroundColor || '#fff0c7'} : null"
            role="rowheader"
        >
            <button
                type="button"
                class="workforce-plan-employee__profile"
                @click="api.openEmployeeInspector(employee)"
            >
                <span class="workforce-plan-employee__name">
                    <strong>
                        <employee-formatted-name
                            :employee="employee"
                        ></employee-formatted-name>
                    </strong>
                    <em v-if="employee.citizenship">{{ employee.citizenship }}</em>
                    <employee-hiring-badge :value="employee.hiring_form" :show-tooltip="false" />
                    <em
                        v-if="employee.import_match_status && employee.import_match_status !== 'matched'"
                        class="workforce-plan-employee__match"
                    >
                        {{ api.importMatchLabel(employee.import_match_status) }}
                    </em>
                </span>
                <span class="workforce-plan-employee__skills">
                    <strong v-if="isItr" class="workforce-plan-employee__itr-label">ИТР</strong>
                    {{ api.employeeSkillLabel(employee) }}
                </span>
            </button>
        </div>
        <button
            v-if="currentStatusWeek"
            :id="api.cellDomId(employee, currentStatusWeek, 'current')"
            type="button"
            class="workforce-plan-cell workforce-plan-current-cell"
            :class="currentCellPresentation.classes"
            :style="currentCellPresentation.style"
            :title="currentCellPresentation.title"
            role="gridcell"
            @mousedown.prevent="api.startCellDrag(employee, currentStatusWeek, $event)"
            @mouseenter="api.extendCellDrag(employee, currentStatusWeek, $event)"
            @click="api.handleCellClick(employee, currentStatusWeek, $event)"
        >
            <span>{{ currentCellPresentation.label }}</span>
            <b
                v-if="currentCellPresentation.arrival"
                class="workforce-plan-cell__arrival"
                aria-label="Фактическое прибытие подтверждено"
            >✓</b>
            <b
                v-else-if="currentCellPresentation.baseline"
                class="workforce-plan-cell__baseline"
                aria-label="Присутствие принято по табелю при переносе"
            >Т</b>
            <small v-if="currentCellPresentation.remoteInitials">
                {{ currentCellPresentation.remoteInitials }}
            </small>
        </button>
        <div
            v-else
            class="workforce-plan-cell workforce-plan-current-cell workforce-plan-cell--missing"
            role="gridcell"
        ></div>
        <button
            v-for="cell in weekCells"
            :id="api.cellDomId(employee, cell.week)"
            :key="cell.week.key"
            type="button"
            class="workforce-plan-cell"
            :class="cell.presentation.classes"
            :style="cell.presentation.style"
            :title="cell.presentation.title"
            role="gridcell"
            @mousedown.prevent="api.startCellDrag(employee, cell.week, $event)"
            @mouseenter="api.extendCellDrag(employee, cell.week, $event)"
            @click="api.handleCellClick(employee, cell.week, $event)"
        >
            <span>{{ cell.presentation.label }}</span>
            <b
                v-if="cell.presentation.arrival"
                class="workforce-plan-cell__arrival"
                aria-label="Фактическое прибытие подтверждено"
            >✓</b>
            <small v-if="cell.presentation.remoteInitials">
                {{ cell.presentation.remoteInitials }}
            </small>
        </button>
    </div>
</template>

<script>
import EmployeeHiringBadge from './EmployeeHiringBadge.vue';
import EmployeeFormattedName from './EmployeeFormattedName.vue';

export default {
    name: 'EmployeeWorkforcePlanRow',
    components: {
        EmployeeHiringBadge,EmployeeFormattedName},
    computed: {
        isItr() {
            return [this.employee.itr_building_ids, this.employee.itr_object_group_ids]
                .some(ids => Array.isArray(ids) && ids.length > 0);
        },
        isOutOfPlanning() {
            return Boolean(this.currentCellPresentation
                && this.currentCellPresentation.classes['workforce-plan-cell--loupe']);
        },
        employeeTitle() {
            const title = this.api.employeeRowTitle(this.employee);
            return [
                this.isOutOfPlanning ? 'Выбыл из планирования на этой неделе' : null,
                title,
                this.isItr ? 'ИТР — инженерно-технический работник' : null
            ].filter(Boolean).join('\n');
        },
        currentCellPresentation() {
            void this.rowHasSelection;
            void this.rowSelected;
            return this.currentStatusWeek
                ? this.api.cellPresentation(
                    this.employee,
                    this.currentStatusWeek,
                    true
                )
                : null;
        },
        weekCells() {
            // Выделение рисуется напрямую в DOM во время протягивания. Зависимость
            // нужна, чтобы Vue не вернул устаревшие классы при смене состояния строки.
            void this.rowHasSelection;
            void this.rowSelected;
            return this.weeks.map(week => ({
                week,
                presentation: this.api.cellPresentation(this.employee, week, false)
            }));
        }
    },
    props: {
        api: {type: Object, required: true},
        employee: {type: Object, required: true},
        inspected: {type: Boolean, default: false},
        focused: {type: Boolean, default: false},
        rowNumber: {type: Number, required: true},
        rowHasSelection: {type: Boolean, default: false},
        rowSelected: {type: Boolean, default: false},
        weeks: {type: Array, required: true},
        currentStatusWeek: {type: Object, default: null}
    }
};
</script>

<style scoped>
.workforce-plan-row-fragment {
    display: grid;
    grid-column: 1 / -1;
    grid-template-columns: var(--plan-columns);
    min-width: 0;
}

.workforce-plan-row-selector,
.workforce-plan-employee,
.workforce-plan-cell {
    min-width: 0;
    height: 28px;
    border: 0;
    border-right: 1px solid #e4e9ee;
    border-bottom: 1px solid #e4e9ee;
    background: #fff;
}

.workforce-plan-employee {
    position: sticky;
    z-index: 4;
    left: var(--plan-row-selector-width, 14px);
    display: flex;
    overflow: hidden;
    height: 30px;
    padding: 0;
    line-height: 1;
    text-align: left;
    box-shadow: 2px 0 3px rgba(39, 58, 75, 0.06);
}

.workforce-plan-row-selector,
.workforce-plan-employee__profile {
    min-width: 0;
    height: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
}

.workforce-plan-row-selector {
    position: sticky;
    z-index: 4;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 1px solid #ced8e1;
    background: #f0f4f7;
    color: #748697;
    cursor: pointer;
    font-size: 0.4rem;
    font-weight: 800;
}

.workforce-plan-row-selector:hover,
.workforce-plan-row-selector:focus-visible {
    background: #dceaf6;
    color: #155f99;
    outline: 0;
}

.workforce-plan-row-selector.is-active {
    background: #9dccf2;
    color: #0f4e7d;
    box-shadow: inset 0 0 0 1px #1976bd;
}

.workforce-plan-row-selector.is-selected {
    background: #1976bd;
    color: #fff;
}

.workforce-plan-employee__profile {
    display: flex;
    width: 100%;
    overflow: hidden;
    padding: 2px 6px;
    flex-direction: column;
    gap: 1px;
    justify-content: center;
    line-height: 1;
    text-align: left;
    cursor: pointer;
}

.workforce-plan-employee.workforce-plan-employee--loupe {
    background: var(--employee-loupe-background, #fff0c7);
}

.workforce-plan-employee__profile:hover { background: rgba(25, 118, 189, 0.06); }

.workforce-plan-employee strong,
.workforce-plan-employee span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workforce-plan-employee__name {
    display: flex !important;
    min-height: 13px;
    align-items: baseline;
    flex: 0 0 13px;
    gap: 5px;
    line-height: 13px;
}

.workforce-plan-employee__name strong {
    min-width: 0;
    overflow: hidden;
    line-height: 13px;
    text-overflow: ellipsis;
}

.workforce-plan-employee__name em {
    flex: 0 0 auto;
    padding: 1px 4px;
    border-radius: 3px;
    background: #e8edf2;
    color: #52677a;
    font-size: 0.5rem;
    font-style: normal;
    font-weight: 800;
}

.workforce-plan-employee__name .employee-hiring-badge {
    font-size: 0.5rem;
    line-height: 13px;
    margin-left: 0;
}

.workforce-plan-employee__name .workforce-plan-employee__match {
    max-width: 96px;
    overflow: hidden;
    background: #fff0c7;
    color: #7a5400;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workforce-plan-employee__name strong,
.workforce-plan-employee__name strong span {
    color: #1f3141;
    font-size: 0.75rem;
    font-weight: 800;
}
.workforce-plan-employee__name .employee-formatted-name--full {
    color: #30485c;
    font-size: 0.57rem;
    font-weight: 800;
}
.workforce-plan-employee span { color: #677b8e; font-size: 0.57rem; font-weight: 700; }
.workforce-plan-row-selector > span {
    color: inherit;
    font: inherit;
}
.workforce-plan-employee .workforce-plan-employee__skills {
    min-height: 10px;
    flex: 0 0 10px;
    color: #526a80;
    line-height: 10px;
}
.workforce-plan-employee__skills .workforce-plan-employee__itr-label {
    display: inline-block;
    margin-right: 4px;
    padding: 0 4px;
    border-radius: 3px;
    background: #1976bd;
    color: #fff;
    font-size: 0.5rem;
    font-weight: 800;
    line-height: 10px;
    vertical-align: top;
}

.workforce-plan-cell {
    position: relative;
    display: flex;
    overflow: hidden;
    height: 30px;
    padding: 0 1px;
    align-items: center;
    color: #32475b;
    font-size: 0.61rem;
    font-weight: 700;
    justify-content: center;
    line-height: 1;
    text-overflow: ellipsis;
    user-select: none;
    white-space: nowrap;
}

.workforce-plan-cell > span {
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workforce-plan-cell__arrival,
.workforce-plan-cell__baseline {
    position: absolute;
    left: 1px;
    top: 1px;
    display: inline-flex;
    width: 10px;
    height: 10px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #218c57;
    color: #fff;
    font-size: 0.48rem;
    line-height: 1;
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.85);
}

.workforce-plan-cell__baseline {
    left: auto;
    right: 1px;
    border-radius: 2px;
    background: #276a9b;
}

.workforce-plan-current-cell {
    position: sticky;
    z-index: 4;
    left: var(--plan-employee-width, 220px);
    border-right: 4px solid #5f778d;
    box-shadow: 2px 0 3px rgba(39, 58, 75, 0.08);
}

.workforce-plan-cell:hover { cursor: crosshair; filter: brightness(0.96); }
.workforce-plan-cell--missing { background: #fff; }
.workforce-plan-cell--object { background: var(--plan-color-dff1e5, #dff1e5); color: var(--plan-text-dff1e5, #21653d); }
.workforce-plan-cell--object_group { background: var(--plan-color-e8f3ce, #e8f3ce); color: var(--plan-text-e8f3ce, #557814); }
.workforce-plan-cell--planned_object {
    border: 1px dashed #21855a;
    background: var(--plan-color-e6f5eb, #e6f5eb);
    color: var(--plan-text-e6f5eb, #17603f);
}
.workforce-plan-cell--requires_distribution { background: var(--plan-color-fde4e2, #fde4e2); color: var(--plan-text-fde4e2, #9f2922); }
.workforce-plan-cell--rest { background: var(--plan-color-e2e2e2, #e2e2e2); color: var(--plan-text-e2e2e2, #4b4b4b); }
.workforce-plan-cell--loupe { background: var(--plan-color-fff0c7, #fff0c7); color: var(--plan-text-fff0c7, #86610b); }
.workforce-plan-cell--mixed::after {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 0;
    height: 0;
    border-bottom: 8px solid #7651a8;
    border-left: 8px solid transparent;
    content: '';
}
.workforce-plan-cell--month {
    border-left: 2px solid #b6c3cf;
    /* Компенсируем утолщение рамки, чтобы не обрезать четырёхбуквенные коды. */
    padding-left: 0;
}
.workforce-plan-cell--today {
    z-index: 1;
    outline: 2px solid #f6a609;
    outline-offset: -2px;
}
.workforce-plan-cell--selected { box-shadow: inset 0 0 0 2px #1976bd; }
.workforce-plan-cell--remote { box-shadow: inset 0 0 0 3px var(--plan-presence-color); }
.workforce-plan-cell--selected.workforce-plan-cell--remote {
    box-shadow: inset 0 0 0 3px #1976bd, inset 0 -5px 0 var(--plan-presence-color);
}
.workforce-plan-cell small {
    position: absolute;
    right: 1px;
    top: 1px;
    padding: 0 2px;
    border-radius: 3px;
    background: var(--plan-presence-color);
    color: #fff;
    font-size: 0.48rem;
}

.workforce-plan-row-fragment--focused > *,
.workforce-plan-row-fragment--inspected > .workforce-plan-row-selector,
.workforce-plan-row-fragment--inspected > .workforce-plan-employee,
.workforce-plan-row-fragment--inspected > .workforce-plan-cell {
    background-image: linear-gradient(
        rgba(25, 118, 189, 0.12),
        rgba(25, 118, 189, 0.12)
    );
}

.workforce-plan-row-fragment--focused > *::before,
.workforce-plan-row-fragment--inspected > .workforce-plan-row-selector::before,
.workforce-plan-row-fragment--inspected > .workforce-plan-employee::before,
.workforce-plan-row-fragment--inspected > .workforce-plan-cell::before {
    position: absolute;
    z-index: 2;
    inset: 0;
    box-sizing: border-box;
    border-top: 2px solid #1976bd;
    border-bottom: 2px solid #1976bd;
    content: '';
    pointer-events: none;
}

.workforce-plan-row-fragment--focused > .workforce-plan-row-selector::before,
.workforce-plan-row-fragment--inspected > .workforce-plan-row-selector::before {
    border-left: 3px solid #1976bd;
}

.workforce-plan-row-fragment--focused > .workforce-plan-cell:last-child::before,
.workforce-plan-row-fragment--inspected > .workforce-plan-cell:last-child::before {
    border-right: 2px solid #1976bd;
}

.workforce-plan-row-fragment--row-selected > .workforce-plan-employee {
    background-color: #e7f2fc;
    box-shadow: inset 0 0 0 2px #1976bd, 2px 0 3px rgba(39, 58, 75, 0.06);
}

.workforce-plan-row-fragment--row-selected > .workforce-plan-row-selector {
    background: #1976bd;
    color: #fff;
    box-shadow: inset 0 0 0 1px #0b568f;
}

@media (max-width: 767.98px),
    (hover: none) and (pointer: coarse) and (max-height: 767.98px) {
    .workforce-plan-row-selector,
    .workforce-plan-employee,
    .workforce-plan-cell { height: 38px; }
    .workforce-plan-employee {
        position: relative;
        z-index: 1;
        left: auto;
        padding: 0;
        box-shadow: none;
    }
    .workforce-plan-row-selector { font-size: 0.35rem; }
    .workforce-plan-employee__profile { padding: 2px 3px; }
    .workforce-plan-employee__name {
        min-height: 15px;
        flex-basis: 15px;
        line-height: 15px;
    }
    .workforce-plan-employee__name { gap: 3px; }
    .workforce-plan-employee__name strong { line-height: 15px; }
    .workforce-plan-employee__name em { padding: 0 2px; font-size: 0.46rem; }
    .workforce-plan-employee__name strong,
    .workforce-plan-employee__name strong span { font-size: 0.7rem; }
    .workforce-plan-employee__name .employee-formatted-name--full {
        font-size: 0.5rem;
    }
    .workforce-plan-employee span { font-size: 0.5rem; }
    .workforce-plan-employee .workforce-plan-employee__skills {
        min-height: 11px;
        flex-basis: 11px;
        line-height: 11px;
    }
    .workforce-plan-cell {
        font-size: 0.62rem;
        touch-action: pan-x pan-y;
    }
    .workforce-plan-current-cell {
        position: relative;
        z-index: 1;
        left: auto;
        box-shadow: none;
    }
}
</style>
